/**
 * MODULE: _shared/frontend-shell/src/hooks/useMobilePushNotificationHandler.ts
 * PURPOSE: Navigate on mobile push notification tap (Sprint 11 mobile signals extends Sprint 10 B-1).
 * DEPENDENCIES: react-router-dom, @shared/lib/frontend/mobile/mobilePushNavigation
 * PUBLIC_API: useMobilePushNotificationHandler
 * SIDE_EFFECTS: client-side navigation; markSignalsSeen on sales_signal deep links
 * AGENT_SAFETY: SAFE
 * RECENT: Sprint 11 [2.2.1082] — type=sales_signal → /activity?filter=signals
 */

import {
 MOBILE_PUSH_TAP_EVENT,
 resolvePushDeepLink,
 type MobilePushTapPayload,
} from '@shared/lib/frontend/mobile/mobilePushNavigation';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { markSignalsSeen } from '../pages/activity/useUnreadSignals';

function parseServiceWorkerMessage(data: unknown): MobilePushTapPayload | null {
 if (!data || typeof data !== 'object') return null;
 const record = data as Record<string, unknown>;
 if (record.type === 'MOBILE_PUSH_TAP' && record.payload && typeof record.payload === 'object') {
 return record.payload as MobilePushTapPayload;
 }
 return null;
}

export function useMobilePushNotificationHandler(): void {
 const navigate = useNavigate();

 const handlePayload = useCallback(
 (payload: MobilePushTapPayload) => {
 const path = resolvePushDeepLink(payload);
 if (!path) return;
 if (path.includes('filter=signals')) {
 markSignalsSeen();
 }
 navigate(path);
 },
 [navigate]
 );

 useEffect(() => {
 const onCustomEvent = (event: Event) => {
 const detail = (event as CustomEvent<MobilePushTapPayload>).detail;
 if (detail) handlePayload(detail);
 };
 window.addEventListener(MOBILE_PUSH_TAP_EVENT, onCustomEvent);
 return () => window.removeEventListener(MOBILE_PUSH_TAP_EVENT, onCustomEvent);
 }, [handlePayload]);

 useEffect(() => {
 if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

 const onSwMessage = (event: MessageEvent) => {
 const payload = parseServiceWorkerMessage(event.data);
 if (payload) handlePayload(payload);
 };

 navigator.serviceWorker.addEventListener('message', onSwMessage);
 return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
 }, [handlePayload]);
}
